import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Chip, EmptyState, HorizontalRail, Skeleton } from "../../design-v2";
import {
  EXERCISE_RESULT_THUMBNAIL_SIZE,
  ExerciseResultRow,
} from "../builders";
import { getFilterPillLabel } from "./searchFilters";

// Tier-3 builder presentation seam. Phase 7A lifted the search presentation out
// of `ManualWorkoutEditor` verbatim and Phase 7B migrated that same seam to
// Design V2. There is ONE panel and ONE search behaviour.
//
// The interaction architecture is frozen (blueprint D23 / section 4.1) and this
// component owns none of it: query state, filter state, the 250ms debounce, the
// fetch effect and its timing, the search limits, `rankExercises` and its
// memoisation, User Exercise Pool semantics, the excluded-match fallback,
// `activeSearchTarget` lane targeting, `data-superset-block` outside-click
// targeting and the past-occurrence lock all remain in the container. This
// component renders what it is given and reports what was pressed.


// Enough rows to read as a list rather than a blank panel, never so many that
// the skeleton is taller than the results region it stands in for.
const SKELETON_ROW_COUNT = 4;

// The first rows of an opened panel are genuinely above the fold; the rest stay
// lazy. Matches the section 9.3 loading rule.
const EAGER_THUMBNAIL_ROW_COUNT = 4;

function ChevronIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 6.5 8 10.5l4-4" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 7h16M7 12h10M10 17h4" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

// Edge-aware scroll fades (presentation only). A fade means "there is more
// content hidden in this direction" -- so it must disappear once the user has
// actually reached that end, rather than sitting there unconditionally. This
// tracks nothing about search/filter state: it only reads the DOM scroll
// metrics of the panel's own two scrollable regions (the filter rail and the
// result list) and reflects them as `data-can-scroll-*` attributes the CSS
// keys its mask-image off. A small epsilon absorbs the subpixel/rounding
// noise real browsers report for scrollLeft/scrollWidth/etc.
const SCROLL_EDGE_EPSILON = 1;
const NO_SCROLL_EDGES = { start: false, end: false };

const HORIZONTAL_SCROLL_METRICS = {
  position: "scrollLeft",
  viewport: "clientWidth",
  extent: "scrollWidth",
};

const VERTICAL_SCROLL_METRICS = {
  position: "scrollTop",
  viewport: "clientHeight",
  extent: "scrollHeight",
};

function readScrollEdges(element, metrics) {
  if (!element) {
    return NO_SCROLL_EDGES;
  }

  const position = element[metrics.position];
  const viewport = element[metrics.viewport];
  const extent = element[metrics.extent];

  return {
    start: position > SCROLL_EDGE_EPSILON,
    end: position + viewport < extent - SCROLL_EDGE_EPSILON,
  };
}

// Either superset lane can be the one being filled now that a populated lane
// can be cleared, so the banner names the lane the container is actually
// targeting. The lane index is the same one `activeSearchTarget` already
// carries; nothing about the targeting or the search itself changes.
function getSearchTargetLaneLabel(activeSearchTarget) {
  const laneIndex = Number(activeSearchTarget?.exerciseIndex);
  return Number.isInteger(laneIndex) && laneIndex >= 0 ? `A${laneIndex + 1}` : "A2";
}

function edgesEqual(a, b) {
  return a.start === b.start && a.end === b.end;
}

export default function ExerciseSearchPanel({
  containerRef,
  className = "",
  searchQuery,
  onSearchQueryChange,
  onSearchFocus,
  onClearSearch,
  disabled,
  shouldShowSearchPanel,
  activeSearchTarget,
  onCancelActiveSearchTarget,
  visibleFilters,
  searchFilters,
  openFilterMenu,
  onToggleFilterMenu,
  onFilterClear,
  onFilterValueToggle,
  isLoadingExercises,
  exerciseError,
  exerciseEmptyMessage,
  visibleExerciseResults,
  onExerciseResultClick,
  trackResultScrollEdges = true,
  animatePanelReveal = true,
  skeletonRowCount = SKELETON_ROW_COUNT,
  // Optional presentation extras (Workout Log pickers): per-row tags, the row
  // glyph, and one node rendered between the filters and the results (a result
  // count or heading). The search field always has an accessible name.
  getResultTags = null,
  resultActionIcon = "add",
  resultsHeader = null,
  // One node rendered inside the results list, after the rows (the Workout
  // Log pickers' next-page indicator). Null (the builder) renders nothing.
  resultsFooter = null,
  // Optional compact filter disclosure (Workout Log pickers, keyboard open):
  // `{ expanded, onToggle, activeCount, onClearAll }`. The rail is then hidden
  // behind one control inside the search field and revealed inline on demand.
  // Null (the builder) keeps the rail permanently visible, exactly as before.
  filterDisclosure = null,
  searchLabel = "Search exercises",
}) {
  // Edge-aware fades: local presentation state only, unrelated to the
  // query/filter/result state the container owns.
  const filtersWrapperRef = useRef(null);
  const resultsRef = useRef(null);
  const hasOpenedPanelRef = useRef(false);
  const lastOpenFilterRef = useRef(null);
  const [railEdges, setRailEdges] = useState(NO_SCROLL_EDGES);
  const [resultsEdges, setResultsEdges] = useState(NO_SCROLL_EDGES);

  const measureRailEdges = useCallback((target) => {
    setRailEdges((previous) => {
      const next = readScrollEdges(target, HORIZONTAL_SCROLL_METRICS);
      return edgesEqual(previous, next) ? previous : next;
    });
  }, []);

  const measureResultsEdges = useCallback((target) => {
    setResultsEdges((previous) => {
      const next = readScrollEdges(target, VERTICAL_SCROLL_METRICS);
      return edgesEqual(previous, next) ? previous : next;
    });
  }, []);

  // Re-measures on mount, whenever the rail's own box is resized (a viewport
  // width change), and whenever the filters/selected values change (the pill
  // set or a pill's own label width can change what actually overflows).
  useLayoutEffect(() => {
    const rail = filtersWrapperRef.current?.querySelector(
      ".lz-builder-search__filter-rail"
    );

    if (!rail) {
      setRailEdges((previous) =>
        edgesEqual(previous, NO_SCROLL_EDGES) ? previous : NO_SCROLL_EDGES
      );
      return undefined;
    }

    measureRailEdges(rail);

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => measureRailEdges(rail));
    observer.observe(rail);
    return () => observer.disconnect();
  }, [shouldShowSearchPanel, visibleFilters, searchFilters, measureRailEdges]);

  // Re-measures on mount, whenever the results list's own box is resized (a
  // viewport width/height change), and whenever the result set or its
  // loading/error/empty presentation changes (all of which change what the
  // list actually contains and therefore whether it overflows).
  useLayoutEffect(() => {
    if (!trackResultScrollEdges) {
      setResultsEdges((previous) =>
        edgesEqual(previous, NO_SCROLL_EDGES) ? previous : NO_SCROLL_EDGES
      );
      return undefined;
    }

    const results = resultsRef.current;

    if (!results) {
      setResultsEdges((previous) =>
        edgesEqual(previous, NO_SCROLL_EDGES) ? previous : NO_SCROLL_EDGES
      );
      return undefined;
    }

    measureResultsEdges(results);

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => measureResultsEdges(results));
    observer.observe(results);
    return () => observer.disconnect();
  }, [
    shouldShowSearchPanel,
    visibleExerciseResults,
    isLoadingExercises,
    exerciseError,
    exerciseEmptyMessage,
    measureResultsEdges,
    trackResultScrollEdges,
  ]);

  const onRailScroll = useCallback(
    (event) => measureRailEdges(event.currentTarget),
    [measureRailEdges]
  );
  const onResultsScroll = useCallback(
    (event) => measureResultsEdges(event.currentTarget),
    [measureResultsEdges]
  );

  const openFilter = visibleFilters.find(
    (filter) => filter.key === openFilterMenu
  );

  // Both reveals below animate in AND out, which needs the content to still
  // be in the DOM while it closes. Two monotonic presentation refs buy that
  // without touching the search architecture: nothing is rendered until the
  // user has actually opened it, and once rendered a closed region is
  // `aria-hidden` + `inert` + `visibility: hidden`, so it is exactly as
  // unreachable as the unmounted markup it replaces -- for pointers, for
  // assistive technology and for `getByRole`. A panel that has never been
  // opened (including one locked by a past occurrence) is still absent.
  if (shouldShowSearchPanel) {
    hasOpenedPanelRef.current = true;
  }

  if (openFilter) {
    lastOpenFilterRef.current = openFilter;
  }

  const isPanelRendered = shouldShowSearchPanel || hasOpenedPanelRef.current;
  // While the options close, the filter they belong to is already gone from
  // `openFilterMenu`, so the last one opened is what keeps rendering.
  const revealedFilter = openFilter || lastOpenFilterRef.current;
  const revealedFilterKey = revealedFilter?.key;

  const compactFilters = Boolean(filterDisclosure);
  const filtersHidden = compactFilters && !filterDisclosure.expanded;
  const activeFilterCount = compactFilters ? Number(filterDisclosure.activeCount) || 0 : 0;
  const filtersRegionId = "lz-builder-search-filters";

  return (
    <div ref={containerRef} className={className}>
      <div className="lz-builder-search__field">
        <span className="lz-builder-search__field-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="6" />
            <path d="M15.5 15.5 20 20" />
          </svg>
        </span>
        <input
          type="text"
          placeholder="Search exercises..."
          aria-label={searchLabel}
          value={searchQuery}
          disabled={disabled}
          onChange={(e) => onSearchQueryChange(e.target.value)}
          onFocus={onSearchFocus}
          className="lz-builder-search__input"
        />
        {searchQuery.trim() && (
          <button
            type="button"
            disabled={disabled}
            onClick={onClearSearch}
            className="lz-builder-search__clear"
            aria-label="Clear search"
          >
            <CloseIcon />
          </button>
        )}
        {compactFilters && (
          <button
            type="button"
            disabled={disabled}
            onClick={filterDisclosure.onToggle}
            className="lz-builder-search__filter-toggle"
            aria-expanded={!filtersHidden}
            aria-controls={filtersRegionId}
            aria-label={activeFilterCount
              ? `Filters, ${activeFilterCount} active`
              : "Filters"}
            data-active={activeFilterCount ? "true" : undefined}
          >
            <FilterIcon />
            {activeFilterCount ? (
              <span className="lz-builder-search__filter-toggle-count" aria-hidden="true">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
        )}
      </div>

      {isPanelRendered && (
        <div
          className={`lz-builder-collapse lz-builder-search__collapse${
            animatePanelReveal ? "" : " lz-builder-search__collapse--static"
          }`}
          data-expanded={shouldShowSearchPanel ? "true" : "false"}
          aria-hidden={shouldShowSearchPanel ? undefined : "true"}
          inert={shouldShowSearchPanel ? undefined : ""}
        >
          <div className="lz-builder-collapse__inner">
            <div className="lz-builder-search__panel">
          {activeSearchTarget && (
            <div className="lz-builder-search__target">
              <p className="lz-builder-search__target-label">
                {`Selecting exercise for ${getSearchTargetLaneLabel(activeSearchTarget)}`}
              </p>
              <button
                type="button"
                onClick={onCancelActiveSearchTarget}
                className="lz-builder-search__target-cancel"
              >
                Cancel
              </button>
            </div>
          )}

          <div
            className="lz-builder-search__filters"
            ref={filtersWrapperRef}
            id={compactFilters ? filtersRegionId : undefined}
            hidden={filtersHidden || undefined}
          >
            <HorizontalRail
              className="lz-builder-search__filter-rail"
              aria-label="Exercise filters"
              data-can-scroll-left={railEdges.start ? "true" : undefined}
              data-can-scroll-right={railEdges.end ? "true" : undefined}
              onScroll={onRailScroll}
            >
              {visibleFilters.map(({ key: filterKey, label, options }) => {
                const isActive = searchFilters[filterKey].length > 0;

                return (
                  <button
                    key={filterKey}
                    type="button"
                    disabled={disabled}
                    onClick={() => onToggleFilterMenu(filterKey)}
                    aria-expanded={openFilterMenu === filterKey}
                    data-active={isActive ? "true" : undefined}
                    className="lz-builder-search__filter"
                  >
                    <span className="lz-builder-search__filter-label">
                      {getFilterPillLabel(label, searchFilters[filterKey], options)}
                    </span>
                    {isActive ? (
                      <span
                        onClick={(event) => onFilterClear(event, filterKey)}
                        className="lz-builder-search__filter-icon"
                        role="button"
                        aria-label={`Clear ${label} filter`}
                      >
                        <CloseIcon />
                      </span>
                    ) : (
                      <span className="lz-builder-search__filter-icon">
                        <ChevronIcon />
                      </span>
                    )}
                  </button>
                );
              })}
              {compactFilters && activeFilterCount > 0 && filterDisclosure.onClearAll ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={filterDisclosure.onClearAll}
                  className="lz-builder-search__filter lz-builder-search__filter--clear-all"
                >
                  <span className="lz-builder-search__filter-label">Clear all</span>
                </button>
              ) : null}
            </HorizontalRail>

            {revealedFilter && (
              <div
                className="lz-builder-collapse lz-builder-search__filter-collapse"
                data-expanded={openFilter ? "true" : "false"}
                aria-hidden={openFilter ? undefined : "true"}
                inert={openFilter ? undefined : ""}
              >
                <div className="lz-builder-collapse__inner">
                  <div className="lz-builder-search__filter-options">
                    {revealedFilter.options.map((option) => (
                      <Chip
                        key={option.value}
                        disabled={disabled}
                        selected={searchFilters[revealedFilterKey].includes(
                          option.value
                        )}
                        onClick={() =>
                          onFilterValueToggle(revealedFilterKey, option.value)
                        }
                      >
                        {option.label}
                      </Chip>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {resultsHeader}

          <div
            className="lz-builder-search__results"
            ref={resultsRef}
            data-can-scroll-up={resultsEdges.start ? "true" : undefined}
            data-can-scroll-down={resultsEdges.end ? "true" : undefined}
            onScroll={trackResultScrollEdges ? onResultsScroll : undefined}
          >
            {isLoadingExercises && (
              <div
                className="lz-builder-search__skeletons"
                data-testid="exercise-search-skeletons"
              >
                {Array.from({ length: skeletonRowCount }, (_, index) => (
                  <div key={index} className="lz-builder-search__skeleton-row">
                    <Skeleton
                      shape="block"
                      width={EXERCISE_RESULT_THUMBNAIL_SIZE}
                      height={EXERCISE_RESULT_THUMBNAIL_SIZE}
                    />
                    <div className="lz-builder-search__skeleton-lines">
                      <Skeleton shape="text" width="62%" />
                      <Skeleton shape="text" width="34%" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoadingExercises && exerciseError && (
              <p className="lz-builder-search__error" role="alert">
                {exerciseError}
              </p>
            )}

            {!isLoadingExercises &&
              !exerciseError &&
              visibleExerciseResults.length === 0 && (
                <EmptyState
                  className="lz-builder-search__empty"
                  titleAs="p"
                  title={exerciseEmptyMessage}
                />
              )}

            {!isLoadingExercises &&
              !exerciseError &&
              visibleExerciseResults.map((exercise, index) => (
                <ExerciseResultRow
                  key={exercise.exerciseId}
                  exercise={exercise}
                  disabled={disabled}
                  thumbnailLoading={
                    index < EAGER_THUMBNAIL_ROW_COUNT ? "eager" : "lazy"
                  }
                  thumbnailFetchPriority={
                    index < EAGER_THUMBNAIL_ROW_COUNT ? "high" : undefined
                  }
                  onSelect={onExerciseResultClick}
                  tags={getResultTags ? getResultTags(exercise) : null}
                  actionIcon={resultActionIcon}
                />
              ))}

            {!isLoadingExercises && !exerciseError ? resultsFooter : null}
          </div>

          <button
            type="button"
            disabled
            className="lz-builder-search__custom"
          >
            <span className="lz-builder-search__custom-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M9.5 3v6.2L4.8 17.6A2 2 0 0 0 6.5 20.6h11a2 2 0 0 0 1.7-3L14.5 9.2V3" />
                <path d="M8.5 3h7M8.2 13.5h7.6" />
              </svg>
            </span>
            <span>Ask for a custom exercise</span>
            <span className="lz-builder-search__custom-badge">BETA</span>
          </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
